import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  Box, Paper, Typography, Button, Chip, IconButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, MenuItem, Select, FormControl, InputLabel,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, CircularProgress, Switch, FormControlLabel,
  Stack, Grid,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PersonIcon from '@mui/icons-material/Person';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';

import ModuleHeader from './components/ModuleHeader';
import { useAuth } from './AuthContext';
import { API_URL } from './config';

// Redesenho 27/10/2026: faixa Hoje + abas Por vir / Agentes em campo / Já passou.
// Regras de data em ./afastamentos/afastamentosDatas.ts (testáveis sem tela).
import FaixaHoje from './afastamentos/FaixaHoje';
import AbasAfastamentos, { Aba, lerAbaSalva, salvarAba } from './afastamentos/AbasAfastamentos';
import ListaPorVir from './afastamentos/ListaPorVir';
import CalendarioEquipe from './afastamentos/CalendarioEquipe';
import TabelaJaPassou from './afastamentos/TabelaJaPassou';
import {
  hojeLocal, situacao, alertasConferir, GRUPOS_TIPO, GrupoTipo, codigosDoGrupo, grupoDosCodigos,
} from './afastamentos/afastamentosDatas';
import { AZUL_ABA } from './afastamentos/visual';

// ════════════════════════════════════════════════════════════
// CSS DE IMPRESSÃO (injetado uma única vez)
// ════════════════════════════════════════════════════════════
const PRINT_CSS = `
/* ── BASE ── */
@media screen { .tovis-print-area { display: none; } }
@media print {
  @page { size: A4 portrait; margin: 0; }
  html, body { background: #fff !important; }
  body * { visibility: hidden !important; }
  .tovis-print-area, .tovis-print-area * { visibility: visible !important; }
  .tovis-print-area {
    position: absolute !important; left: 0 !important; top: 0 !important;
    width: 210mm !important; min-height: 297mm !important;
    padding: 13mm 16mm 14mm !important; margin: 0 !important;
    background: #fff !important; color: #1a1a1a !important;
    font-family: Helvetica, "Helvetica Neue", Arial, sans-serif !important;
    font-size: 9.5pt !important; line-height: 1.5 !important;
    display: flex !important; flex-direction: column !important;
    -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;
  }
  /* HEADER */
  .tovis-print-area .doc-rule-top { height: 4px; background: #000; margin-bottom: 7mm; }
  .tovis-print-area .doc-wordmark-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10mm; }
  .tovis-print-area .doc-wordmark { display: flex; align-items: center; gap: 8px; font-size: 7.5pt; font-weight: 700; letter-spacing: 0.22em; text-transform: uppercase; color: #1a1a1a; }
  .tovis-print-area .doc-wordmark .wm-mark { width: 5mm; height: 5mm; background: #1a1a1a; color: #fff; font-family: Georgia, serif; font-size: 9pt; display: inline-flex; align-items: center; justify-content: center; letter-spacing: 0; }
  .tovis-print-area .doc-wordmark .wm-divider { color: #bbb; font-weight: 400; }
  .tovis-print-area .doc-doc-id { font-family: "Courier New", monospace; font-size: 7.5pt; color: #888; letter-spacing: 0.08em; }
  /* TITLE */
  .tovis-print-area .doc-title-block { margin-bottom: 6mm; }
  .tovis-print-area .doc-title-kicker { font-size: 8pt; font-weight: 700; letter-spacing: 0.2em; text-transform: uppercase; color: #555; margin-bottom: 2.5mm; }
  .tovis-print-area .doc-title { font-family: Georgia, "Times New Roman", serif; font-size: 24pt; font-weight: 400; line-height: 1.05; color: #111; letter-spacing: -0.012em; max-width: 150mm; margin: 0; }
  .tovis-print-area .doc-title em { font-style: italic; }
  .tovis-print-area .doc-subtitle { font-size: 9.5pt; color: #555; margin-top: 3mm; max-width: 140mm; }
  /* METADATA */
  .tovis-print-area .doc-meta { display: flex; flex-wrap: wrap; margin-bottom: 7mm; border-top: 0.5pt solid #1a1a1a; border-bottom: 0.5pt solid #d9d9d9; }
  .tovis-print-area .meta-cell { flex: 1 1 0; min-width: 38mm; padding: 3mm 4mm 3mm 0; border-right: 0.4pt solid #e2e2e2; }
  .tovis-print-area .meta-cell:last-child { border-right: none; }
  .tovis-print-area .meta-cell + .meta-cell { padding-left: 4mm; }
  .tovis-print-area .meta-cell .label { font-size: 7pt; font-weight: 700; letter-spacing: 0.16em; text-transform: uppercase; color: #888; margin-bottom: 1.2mm; }
  .tovis-print-area .meta-cell .value { font-size: 9pt; color: #1a1a1a; }
  .tovis-print-area .meta-cell .mono { font-family: "Courier New", monospace; font-size: 8.5pt; }
  /* SUMMARY */
  .tovis-print-area .doc-summary-title, .tovis-print-area .doc-section-label { font-size: 7.5pt; font-weight: 700; letter-spacing: 0.18em; text-transform: uppercase; color: #555; margin-bottom: 3mm; }
  .tovis-print-area .doc-summary { display: grid; grid-template-columns: repeat(6, 1fr); column-gap: 4mm; margin-bottom: 4mm; padding-bottom: 5mm; border-bottom: 0.5pt solid #d9d9d9; }
  .tovis-print-area .summary-item { padding: 0 4mm; border-left: 0.4pt solid #e2e2e2; }
  .tovis-print-area .summary-item:first-child { padding-left: 0; border-left: none; }
  .tovis-print-area .summary-item .value { font-family: Georgia, "Times New Roman", serif; font-size: 20pt; line-height: 1; color: #111; font-variant-numeric: tabular-nums; letter-spacing: -0.02em; }
  .tovis-print-area .summary-item .unit { font-size: 8pt; color: #888; margin-left: 3px; font-family: Helvetica, Arial, sans-serif; }
  .tovis-print-area .summary-item .label { font-size: 7pt; color: #666; text-transform: uppercase; letter-spacing: 0.06em; margin-top: 2mm; max-width: 22mm; line-height: 1.25; }
  .tovis-print-area .doc-summary-note { margin-bottom: 7mm; font-style: italic; font-size: 8.5pt; color: #666; }
  /* TABLE */
  .tovis-print-area .doc-table { width: 100%; border-collapse: collapse; font-size: 9pt; }
  .tovis-print-area .doc-table thead { display: table-header-group; }
  .tovis-print-area .doc-table tr { page-break-inside: avoid; }
  .tovis-print-area .doc-table thead th { text-align: left; padding: 2.5mm 2mm 2mm 0; border-bottom: 0.6pt solid #1a1a1a; font-size: 7pt; font-weight: 700; letter-spacing: 0.16em; text-transform: uppercase; color: #1a1a1a; background: #fff !important; }
  .tovis-print-area .doc-table tbody td { padding: 3mm 2mm 3mm 0; border-bottom: 0.3pt solid #ececec; vertical-align: top; background: #fff !important; }
  .tovis-print-area .doc-table .col-name { font-weight: 600; }
  .tovis-print-area .doc-table .col-period { font-family: "Courier New", monospace; font-size: 8.5pt; color: #333; white-space: nowrap; }
  .tovis-print-area .doc-table .col-days { text-align: right; font-variant-numeric: tabular-nums; padding-right: 4mm !important; }
  .tovis-print-area .doc-table .col-notes { color: #444; }
  .tovis-print-area .doc-table .col-notes .empty { color: #bbb; }
  /* STATUS PILL */
  .tovis-print-area .status-pill { display: inline-block; padding: 0.6mm 2.2mm; border: 0.6pt solid #1a1a1a; border-radius: 100px; font-size: 7pt; font-weight: 600; letter-spacing: 0.08em; text-transform: uppercase; line-height: 1.2; }
  .tovis-print-area .status-pill.is-oficial { color: #fff !important; background: #1a1a1a !important; }
  .tovis-print-area .status-pill.is-interno { color: #555; border-color: #b0b0b0; }
  /* TOTAL ROW */
  .tovis-print-area .doc-table-foot-row td { border-bottom: none !important; padding-top: 3mm !important; font-size: 8.5pt; color: #555; }
  .tovis-print-area .doc-table-foot-row .count { font-weight: 700; color: #1a1a1a; font-variant-numeric: tabular-nums; }
  /* SIGNATURES */
  .tovis-print-area .doc-signatures { margin-top: auto; padding-top: 18mm; display: grid; grid-template-columns: 1fr 1fr; gap: 24mm; page-break-inside: avoid; }
  .tovis-print-area .sig-line { height: 0.6pt; background: #1a1a1a; margin-bottom: 2mm; }
  .tovis-print-area .sig-name { font-family: Georgia, "Times New Roman", serif; font-size: 10.5pt; color: #1a1a1a; margin-bottom: 0.5mm; }
  .tovis-print-area .sig-role { font-size: 8pt; color: #666; letter-spacing: 0.04em; }
  /* FOOTER */
  .tovis-print-area .doc-footer { margin-top: 9mm; padding-top: 3mm; border-top: 0.4pt solid #d9d9d9; display: flex; justify-content: space-between; font-size: 7.5pt; color: #888; letter-spacing: 0.06em; }
  .tovis-print-area .doc-footer .right { font-family: "Courier New", monospace; }
  .tovis-print-area .no-print { display: none !important; }
}
`;
if (typeof document !== 'undefined') {
  const existing = document.getElementById('tovis-print-style');
  if (existing) existing.remove();
  const _s = document.createElement('style');
  _s.id = 'tovis-print-style';
  _s.appendChild(document.createTextNode(PRINT_CSS));
  document.head.appendChild(_s);
}

// ════════════════════════════════════════════════════════════
// TIPOS
// ════════════════════════════════════════════════════════════

interface Agente {
  id: number;
  nome: string;
  ativo: boolean;
}

interface TipoCatalogo {
  codigo: string;
  label: string;
  categoria: 'ferias' | 'licenca' | 'falta' | 'saida';
  padrao_oficial: boolean;
}

interface Afastamento {
  id: number;
  agente_id: number;
  agente_nome: string | null;
  tipo: string;
  tipo_label: string | null;
  data_inicio: string;
  data_fim: string;
  data_retorno?: string | null;
  hora_inicio: string | null;
  hora_fim: string | null;
  motivo: string | null;
  tem_comprovante: boolean;
  comprovante_obs: string | null;
  oficializado: boolean;
  observacoes_supervisor: string | null;
  criado_por_id: number | null;
  criado_em: string;
  atualizado_em: string;
  dias_corridos: number;
}

// Equipe de campo, lida do Planner Férias (GET /api/planner-ferias/quadro, só leitura):
// mesmo total que o Planner usa no "Em campo".
interface Equipe {
  total: number;
  ids: number[];
}

interface ResumoAno {
  ano: number;
  agente_id: number;
  agente_nome: string;
  agente_matricula?: string | null;
  dias_ferias_gozadas: number;
  dias_ferias_planejadas: number;
  dias_licenca_total: number;
  qtd_faltas_justificadas: number;
  qtd_faltas_injustificadas: number;
  qtd_saidas_turno: number;
  qtd_nao_oficializados: number;
  total_registros: number;
}

interface FormState {
  agente_id: number | '';
  tipo: string;
  data_inicio: string;
  data_fim: string;
  data_retorno: string;
  hora_inicio: string;
  hora_fim: string;
  motivo: string;
  tem_comprovante: boolean;
  comprovante_obs: string;
  oficializado: boolean;
  observacoes_supervisor: string;
}

// ════════════════════════════════════════════════════════════
// CONSTANTES VISUAIS
// ════════════════════════════════════════════════════════════

const HEADER_SX = {
  backgroundColor: '#1D1D1F',
  color: 'white',
  fontWeight: 700,
  fontSize: 11,
  textTransform: 'uppercase' as const,
  letterSpacing: 0.5,
};

const C = {
  azul: '#1B3A6B',
  ambar: '#F59E0B',
  cinza: '#6B7280',
  violeta: '#8B5CF6',
};

const CORES_TIPO: Record<string, { bg: string; cor: string }> = {
  FERIAS_PLANEJADAS:   { bg: '#EDE9FE', cor: '#5B21B6' },  // roxo — agendado
  FERIAS_GOZADAS:      { bg: '#D1FAE5', cor: '#065F46' },  // verde — realizado
  LICENCA_MEDICA:      { bg: '#FEF3C7', cor: '#92400E' },
  LICENCA_MATERNIDADE: { bg: '#FCE7F3', cor: '#9D174D' },
  LICENCA_OUTROS:      { bg: '#F3F4F6', cor: '#374151' },
  LICENCA_PREMIO:      { bg: '#FFFBEB', cor: '#92400E' },  // dourado — benefício/direito
  FALTA_JUSTIFICADA:   { bg: '#FEF9C3', cor: '#854D0E' },
  FALTA_INJUSTIFICADA: { bg: '#FEE2E2', cor: '#991B1B' },
  SAIDA_TURNO:         { bg: '#E0F2FE', cor: '#075985' },  // ciano — movido de roxo
};

const FORM_VAZIO: FormState = {
  agente_id: '',
  tipo: 'FERIAS_PLANEJADAS',
  data_inicio: '',
  data_fim: '',
  data_retorno: '',
  hora_inicio: '',
  hora_fim: '',
  motivo: '',
  tem_comprovante: false,
  comprovante_obs: '',
  oficializado: true,
  observacoes_supervisor: '',
};

// ════════════════════════════════════════════════════════════
// HELPERS
// ════════════════════════════════════════════════════════════

function fmtData(s: string): string {
  if (!s) return '';
  const [a, m, d] = s.split('-');
  return `${d}/${m}/${a.slice(2)}`;
}

function fmtPeriodo(af: Afastamento): string {
  if (af.tipo === 'SAIDA_TURNO') {
    const h1 = (af.hora_inicio || '').slice(0, 5);
    const h2 = (af.hora_fim || '').slice(0, 5);
    if (!h1 && !h2) return fmtData(af.data_inicio);
    if (h1 && !h2)  return `${fmtData(af.data_inicio)} • ${h1} → —`;
    return `${fmtData(af.data_inicio)} • ${h1} → ${h2}`;
  }
  if (af.data_inicio === af.data_fim) return fmtData(af.data_inicio);
  return `${fmtData(af.data_inicio)} → ${fmtData(af.data_fim)}`;
}

function extrairErro(e: unknown, fallback = 'Erro inesperado.'): string {
  const err = e as { response?: { data?: { detail?: unknown } } };
  const detail = err?.response?.data?.detail;
  if (Array.isArray(detail)) {
    const p = detail[0] as { loc?: string[]; msg?: string };
    const campo = p?.loc?.slice(1).join('.') || '';
    const msg = p?.msg || 'Erro de validação';
    return campo ? `${campo}: ${msg}` : msg;
  }
  return (typeof detail === 'string' ? detail : null) || fallback;
}

// ════════════════════════════════════════════════════════════
// SUBCOMPONENTE: KPIMini
// ════════════════════════════════════════════════════════════

interface KPIMiniProps {
  titulo: string;
  valor: number;
  sufixo: string;
  cor: string;
}

function KPIMini({ titulo, valor, sufixo, cor }: KPIMiniProps) {
  return (
    <Box sx={{ borderLeft: `3px solid ${cor}`, pl: 1.2 }}>
      <Typography sx={{ fontSize: 10, fontWeight: 600, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.3 }}>
        {titulo}
      </Typography>
      <Stack direction="row" spacing={0.5} sx={{ alignItems: 'baseline' }}>
        <Typography sx={{ fontSize: 22, fontWeight: 700, color: cor }}>{valor}</Typography>
        {sufixo && <Typography sx={{ fontSize: 11, color: '#6B7280' }}>{sufixo}</Typography>}
      </Stack>
    </Box>
  );
}

// ════════════════════════════════════════════════════════════
// SUBCOMPONENTE: FichaAgente
// ════════════════════════════════════════════════════════════

interface FichaAgenteProps {
  carregando: boolean;
  resumo: ResumoAno | null;
  registros: Afastamento[];
  onEditar: (af: Afastamento) => void;
  onExcluir: (af: Afastamento) => void;
  onVoltar: () => void;
  onImprimir: () => void;
}

function FichaAgente({ carregando, resumo, registros, onEditar, onExcluir, onVoltar, onImprimir }: FichaAgenteProps) {
  const porAno = useMemo(() => {
    const m: Record<number, Afastamento[]> = {};
    for (const r of registros) {
      const ano = parseInt(r.data_inicio.slice(0, 4), 10);
      if (!m[ano]) m[ano] = [];
      m[ano].push(r);
    }
    return m;
  }, [registros]);
  const anosOrdenados = Object.keys(porAno).map(Number).sort((a, b) => b - a);

  if (carregando) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Barra de ação */}
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={onVoltar}
          sx={{ borderRadius: 2 }}
        >
          Voltar para a lista
        </Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant="contained"
          startIcon={<PrintIcon />}
          onClick={onImprimir}
          sx={{ backgroundColor: '#1B3A6B', borderRadius: 2 }}
        >
          Imprimir ficha
        </Button>
      </Stack>

      {/* KPIs anuais */}
      {resumo && (
        <Paper elevation={0} sx={{ p: 2.5, mb: 3, borderRadius: 3, border: '1px solid #E5E7EB' }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#6B7280', textTransform: 'uppercase', letterSpacing: 0.5, mb: 1.5 }}>
            Resumo de {resumo.ano} — {resumo.agente_nome}
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <KPIMini titulo="Férias gozadas"    valor={resumo.dias_ferias_gozadas}        sufixo="dias" cor="#1B3A6B" />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <KPIMini titulo="Férias planejadas"  valor={resumo.dias_ferias_planejadas}     sufixo="dias" cor="#3B82F6" />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <KPIMini titulo="Licenças"           valor={resumo.dias_licenca_total}         sufixo="dias" cor="#F59E0B" />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <KPIMini titulo="Faltas justif."     valor={resumo.qtd_faltas_justificadas}    sufixo=""     cor="#EAB308" />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <KPIMini titulo="Faltas injustif."   valor={resumo.qtd_faltas_injustificadas}  sufixo=""     cor="#EF4444" />
            </Grid>
            <Grid size={{ xs: 6, sm: 4, md: 2 }}>
              <KPIMini titulo="Saídas de turno"    valor={resumo.qtd_saidas_turno}           sufixo=""     cor="#8B5CF6" />
            </Grid>
          </Grid>
          {resumo.qtd_nao_oficializados > 0 && (
            <Typography sx={{ fontSize: 12, color: '#6B7280', mt: 1.5 }}>
              <VisibilityOffIcon sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
              {resumo.qtd_nao_oficializados} {resumo.qtd_nao_oficializados === 1 ? 'registro interno' : 'registros internos'} no ano.
            </Typography>
          )}
        </Paper>
      )}

      {/* Timeline por ano */}
      {registros.length === 0 ? (
        <Paper elevation={0} sx={{ p: 5, borderRadius: 3, border: '1px solid #E5E7EB', textAlign: 'center', color: '#6B7280' }}>
          Este agente ainda não tem registros de afastamento.
        </Paper>
      ) : anosOrdenados.map(ano => (
        <Box key={ano} sx={{ mb: 3 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#1B3A6B', mb: 1, letterSpacing: 0.5 }}>
            {ano} — {porAno[ano].length} {porAno[ano].length === 1 ? 'registro' : 'registros'}
          </Typography>
          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: '1px solid #E5E7EB' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['Tipo', 'Período', 'Dias', 'Motivo', 'Status', 'Ações'].map(col => (
                    <TableCell key={col} sx={HEADER_SX} align={['Dias', 'Ações'].includes(col) ? 'right' : col === 'Status' ? 'center' : 'left'}>
                      {col}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {porAno[ano].map((af, i) => {
                  const cores = CORES_TIPO[af.tipo] || { bg: '#F3F4F6', cor: '#374151' };
                  const naoOficial = !af.oficializado;
                  return (
                    <TableRow
                      key={af.id}
                      sx={{
                        backgroundColor: i % 2 === 0 ? '#fff' : '#F7F7F8',
                        '&:hover': { filter: 'brightness(0.97)' },
                        ...(naoOficial && { borderLeft: '3px dashed #9CA3AF' }),
                      }}
                    >
                      <TableCell>
                        <Chip
                          label={af.tipo_label || af.tipo}
                          size="small"
                          sx={{ backgroundColor: cores.bg, color: cores.cor, fontWeight: 600, fontSize: 11 }}
                        />
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'ui-monospace, monospace', fontSize: 12 }}>
                        {fmtPeriodo(af)}
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{af.dias_corridos}</TableCell>
                      <TableCell sx={{ maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {af.motivo || '—'}
                        {af.tem_comprovante && af.comprovante_obs && (
                          <Typography sx={{ fontSize: 11, color: '#6B7280', mt: 0.3 }}>
                            📎 {af.comprovante_obs}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {naoOficial ? (
                          <Chip
                            icon={<VisibilityOffIcon sx={{ fontSize: '14px !important', color: '#6B7280 !important' }} />}
                            label="Interno"
                            size="small"
                            sx={{ backgroundColor: '#F3F4F6', color: '#374151', fontWeight: 600, fontSize: 10 }}
                          />
                        ) : (
                          <Chip label="Oficial" size="small" sx={{ backgroundColor: '#D1FAE5', color: '#065F46', fontWeight: 600, fontSize: 10 }} />
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => onEditar(af)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => onExcluir(af)}>
                          <DeleteIcon fontSize="small" sx={{ color: '#EF4444' }} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}
    </Box>
  );
}

// ════════════════════════════════════════════════════════════
// SUBCOMPONENTE: RelatorioImpressao
// ════════════════════════════════════════════════════════════

interface RelatorioImpressaoProps {
  modo: 'geral' | 'ficha';
  titulo: string;
  subtitulo?: string;
  filtrosTxt?: string;
  periodoTxt?: string;
  resumo?: ResumoAno | null;
  registros: Afastamento[];
  supervisorNome: string;
}

function gerarDocId(modo: 'geral' | 'ficha'): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const seq = String(Math.floor(Math.random() * 9999)).padStart(4, '0');
  return `${modo === 'ficha' ? 'AFA-IND' : 'AFA'}-${yyyy}-${mm}${dd}-${seq}`;
}

function RelatorioImpressao({ modo, titulo, subtitulo, filtrosTxt, periodoTxt, resumo, registros, supervisorNome }: RelatorioImpressaoProps) {
  const hoje = new Date().toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
  const docId = useMemo(() => gerarDocId(modo), [modo]);

  return (
    <div className="tovis-print-area">
      <div className="doc-rule-top"></div>

      {/* WORDMARK + DOC ID */}
      <div className="doc-wordmark-row">
        <div className="doc-wordmark">
          <span className="wm-mark">T</span>
          <span>Sistema TOVIS</span>
          <span className="wm-divider">/</span>
          <span>Relatório de Afastamentos</span>
        </div>
        <div className="doc-doc-id">DOC&nbsp;Nº&nbsp;{docId}</div>
      </div>

      {/* TÍTULO */}
      <div className="doc-title-block">
        {modo === 'ficha' && (
          <div className="doc-title-kicker">Ficha individual · Exercício {resumo?.ano ?? new Date().getFullYear()}</div>
        )}
        <h1 className="doc-title">
          {modo === 'ficha' ? <em>{titulo}</em> : titulo}
        </h1>
        {subtitulo && <p className="doc-subtitle">{subtitulo}</p>}
      </div>

      {/* METADATA */}
      <div className="doc-meta">
        <div className="meta-cell">
          <div className="label">Emitido em</div>
          <div className="value mono">{hoje}</div>
        </div>
        {modo === 'ficha' ? (
          <>
            <div className="meta-cell">
              <div className="label">Matrícula</div>
              <div className="value mono">{resumo?.agente_matricula || '—'}</div>
            </div>
            <div className="meta-cell">
              <div className="label">Lotação</div>
              <div className="value">ACE Milagres-BA</div>
            </div>
          </>
        ) : (
          <>
            <div className="meta-cell">
              <div className="label">Período</div>
              <div className="value mono">{periodoTxt || 'Todos'}</div>
            </div>
            <div className="meta-cell">
              <div className="label">Município</div>
              <div className="value">Milagres — BA</div>
            </div>
          </>
        )}
        <div className="meta-cell">
          <div className="label">Filtros</div>
          <div className="value">{filtrosTxt || 'Todos os registros'}</div>
        </div>
      </div>

      {/* SUMMARY — só na ficha */}
      {modo === 'ficha' && resumo && (
        <>
          <div className="doc-summary-title">Resumo do ano de {resumo.ano}</div>
          <div className="doc-summary">
            <div className="summary-item">
              <div className="value">{resumo.dias_ferias_gozadas}<span className="unit">d</span></div>
              <div className="label">Férias gozadas</div>
            </div>
            <div className="summary-item">
              <div className="value">{resumo.dias_ferias_planejadas}<span className="unit">d</span></div>
              <div className="label">Férias planejadas</div>
            </div>
            <div className="summary-item">
              <div className="value">{resumo.dias_licenca_total}<span className="unit">d</span></div>
              <div className="label">Licenças</div>
            </div>
            <div className="summary-item">
              <div className="value">{resumo.qtd_faltas_justificadas}</div>
              <div className="label">Faltas justificadas</div>
            </div>
            <div className="summary-item">
              <div className="value">{resumo.qtd_faltas_injustificadas}</div>
              <div className="label">Faltas injustificadas</div>
            </div>
            <div className="summary-item">
              <div className="value">{resumo.qtd_saidas_turno}</div>
              <div className="label">Saídas de turno</div>
            </div>
          </div>
          {resumo.qtd_nao_oficializados > 0 && (
            <div className="doc-summary-note">
              Inclui {resumo.qtd_nao_oficializados} {resumo.qtd_nao_oficializados === 1 ? 'registro interno (não-oficializado)' : 'registros internos (não-oficializados)'}.
            </div>
          )}
        </>
      )}

      {/* TABELA */}
      <div className="doc-section-label">
        {modo === 'ficha' ? 'Registros do exercício' : 'Registros'}
      </div>
      <table className="doc-table">
        <thead>
          <tr>
            {modo === 'geral' && <th style={{ width: '24%' }}>Agente</th>}
            <th style={{ width: modo === 'ficha' ? '24%' : '18%' }}>Tipo</th>
            <th style={{ width: modo === 'ficha' ? '26%' : '21%' }}>Período</th>
            <th style={{ width: '8%' }} className="col-days">Dias</th>
            <th style={{ width: modo === 'ficha' ? '32%' : '22%' }}>Motivo / Observações</th>
            <th style={{ width: '10%', textAlign: 'right' }}>Status</th>
          </tr>
        </thead>
        <tbody>
          {registros.length === 0 ? (
            <tr>
              <td colSpan={modo === 'geral' ? 6 : 5} style={{ fontStyle: 'italic', color: '#888', padding: '6mm 0' }}>
                Nenhum registro encontrado com os filtros aplicados.
              </td>
            </tr>
          ) : registros.map(af => (
            <tr key={af.id}>
              {modo === 'geral' && <td className="col-name">{af.agente_nome}</td>}
              <td>{af.tipo_label || af.tipo}</td>
              <td className="col-period">{fmtPeriodo(af)}</td>
              <td className="col-days">{af.dias_corridos}</td>
              <td className="col-notes">
                {af.motivo ? af.motivo : <span className="empty">—</span>}
                {af.tem_comprovante && af.comprovante_obs && (
                  <div style={{ fontSize: '7.5pt', color: '#777', marginTop: '0.8mm' }}>
                    Comprovante: {af.comprovante_obs}
                  </div>
                )}
                {af.observacoes_supervisor && (
                  <div style={{ fontSize: '7.5pt', color: '#777', marginTop: '0.8mm', fontStyle: 'italic' }}>
                    Obs.: {af.observacoes_supervisor}
                  </div>
                )}
              </td>
              <td style={{ textAlign: 'right' }}>
                <span className={`status-pill ${af.oficializado ? 'is-oficial' : 'is-interno'}`}>
                  {af.oficializado ? 'Oficial' : 'Interno'}
                </span>
              </td>
            </tr>
          ))}
          {registros.length > 0 && (
            <tr className="doc-table-foot-row">
              <td colSpan={modo === 'geral' ? 3 : 2}><span>Total de registros</span></td>
              <td className="count col-days">{registros.length}</td>
              <td colSpan={2}></td>
            </tr>
          )}
        </tbody>
      </table>

      {/* ASSINATURAS */}
      <div className="doc-signatures">
        <div className="sig-block">
          <div className="sig-line"></div>
          <div className="sig-name">{supervisorNome}</div>
          <div className="sig-role">Supervisor — ACE Milagres-BA</div>
        </div>
        <div className="sig-block">
          <div className="sig-line"></div>
          <div className="sig-name">&nbsp;</div>
          <div className="sig-role">Chefe Superior</div>
        </div>
      </div>

      {/* RODAPÉ */}
      <div className="doc-footer">
        <span>Sistema TOVIS — criado por Walber Reis</span>
        <span className="right">Página 1 de 1</span>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ════════════════════════════════════════════════════════════

interface Props {
  onVoltar: () => void;
}

export default function Afastamentos({ onVoltar }: Props) {
  const { token, usuario } = useAuth();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [registros, setRegistros]   = useState<Afastamento[]>([]);
  const [agentes, setAgentes]       = useState<Agente[]>([]);
  const [tipos, setTipos]           = useState<TipoCatalogo[]>([]);
  const [todos, setTodos]           = useState<Afastamento[]>([]);      // sem filtro: faixa Hoje, calendário e alertas
  const [equipe, setEquipe]         = useState<Equipe | null>(null);
  const [aba, setAba]               = useState<Aba>(lerAbaSalva);
  const [maisFiltros, setMaisFiltros] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erro, setErro]             = useState('');
  const [sucesso, setSucesso]       = useState('');

  // Filtros
  const [filtroAgente, setFiltroAgente]               = useState<number | ''>('');
  const [filtroTipos, setFiltroTipos]                 = useState<string[]>([]);
  const [filtroDataDe, setFiltroDataDe]               = useState('');
  const [filtroDataAte, setFiltroDataAte]             = useState('');
  const [soNaoOficializados, setSoNaoOficializados]   = useState(false);

  // Dialog criar/editar
  const [dialogAberto, setDialogAberto] = useState(false);
  const [editando, setEditando]         = useState<Afastamento | null>(null);
  const [form, setForm]                 = useState<FormState>(FORM_VAZIO);
  const [salvando, setSalvando]         = useState(false);
  const [erroForm, setErroForm]         = useState('');

  // Dialog excluir
  const [dialogExcluir, setDialogExcluir] = useState<Afastamento | null>(null);
  const [excluindo, setExcluindo]         = useState(false);

  // Dialog pré-impressão (orienta a desligar cabeçalho do navegador)
  const [dialogImprimir, setDialogImprimir] = useState(false);
  const dispararImpressao = () => {
    setDialogImprimir(false);
    setTimeout(() => window.print(), 150);
  };

  // Vista (lista | ficha) e dados da ficha
  const [vista, setVista]               = useState<'lista' | 'ficha'>('lista');
  const [fichaAgenteId, setFichaAgenteId] = useState<number | null>(null);
  const [fichaRegistros, setFichaRegistros] = useState<Afastamento[]>([]);
  const [fichaResumo, setFichaResumo]   = useState<ResumoAno | null>(null);
  const [fichaCarregando, setFichaCarregando] = useState(false);

  // ── Carregar catálogos (1x)
  useEffect(() => {
    (async () => {
      try {
        const [ag, tp] = await Promise.all([
          axios.get(`${API_URL}/api/agentes/`, { headers }),
          axios.get(`${API_URL}/api/afastamentos/tipos`, { headers }),
        ]);
        const ativos = (ag.data as Agente[]).filter(a => a.ativo).sort((a, b) => a.nome.localeCompare(b.nome));
        setAgentes(ativos);
        setTipos(tp.data as TipoCatalogo[]);
      } catch {
        setErro('Falha ao carregar catálogos.');
      }
    })();
  }, [headers]);

  // ── Carregar lista + resumo
  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const params: Record<string, unknown> = {};
      if (filtroAgente)          params.agente_id            = filtroAgente;
      if (filtroTipos.length)    params.tipo                 = filtroTipos;
      if (filtroDataDe)          params.data_de              = filtroDataDe;
      if (filtroDataAte)         params.data_ate             = filtroDataAte;
      if (soNaoOficializados)    params.so_nao_oficializados = true;

      const lst = await axios.get(`${API_URL}/api/afastamentos`, { headers, params });
      setRegistros(lst.data as Afastamento[]);
    } catch (e) {
      setErro(extrairErro(e, 'Falha ao carregar afastamentos.'));
    } finally {
      setCarregando(false);
    }
  }, [headers, filtroAgente, filtroTipos, filtroDataDe, filtroDataAte, soNaoOficializados]);

  useEffect(() => { carregar(); }, [carregar]);

  // ── Lista completa (mesmo endereço, sem filtro) para a faixa Hoje, o calendário e os alertas
  const carregarTodos = useCallback(async () => {
    try {
      const r = await axios.get(`${API_URL}/api/afastamentos`, { headers });
      setTodos(r.data as Afastamento[]);
    } catch (e) {
      setErro(extrairErro(e, 'Falha ao carregar afastamentos.'));
    }
  }, [headers]);

  useEffect(() => { carregarTodos(); }, [carregarTodos]);

  // ── Equipe de campo: o mesmo total do Planner Férias (só leitura)
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/planner-ferias/quadro`, {
          headers, params: { ano: Number(hojeLocal().slice(0, 4)) },
        });
        const q = r.data as { total_campo: number; agentes: Array<{ id: number; grupo: string }> };
        setEquipe({ total: q.total_campo, ids: q.agentes.filter(a => a.grupo === 'campo').map(a => a.id) });
      } catch {
        setEquipe(null);   // sem o total, a tela mostra só "X fora"
      }
    })();
  }, [headers]);

  const trocarAba = (a: Aba) => { setAba(a); salvarAba(a); };

  // ── Ficha individual do agente
  const abrirFicha = useCallback(async (agente_id: number) => {
    setErro(''); setSucesso(''); setErroForm('');
    setFichaAgenteId(agente_id);
    setVista('ficha');
    setFichaCarregando(true);
    setFichaRegistros([]);
    setFichaResumo(null);
    try {
      const [rg, rs] = await Promise.all([
        axios.get(`${API_URL}/api/afastamentos/agente/${agente_id}`, { headers }),
        axios.get(`${API_URL}/api/afastamentos/agente/${agente_id}/resumo-ano`, { headers }),
      ]);
      setFichaRegistros(rg.data || []);
      setFichaResumo(rs.data);
    } catch (e) {
      setErro(extrairErro(e, 'Falha ao carregar ficha do agente.'));
    } finally {
      setFichaCarregando(false);
    }
  }, [headers]);

  const voltarParaLista = () => {
    setVista('lista');
    setFichaAgenteId(null);
    setFichaRegistros([]);
    setFichaResumo(null);
  };

  const recarregarFichaSeAberta = useCallback(() => {
    if (vista === 'ficha' && fichaAgenteId) abrirFicha(fichaAgenteId);
  }, [vista, fichaAgenteId, abrirFicha]);

  // ── Descrição dos filtros aplicados (para o relatório impresso)
  const filtrosTxt = useMemo(() => {
    const partes: string[] = [];
    if (filtroAgente) {
      const nome = agentes.find(a => a.id === filtroAgente)?.nome;
      partes.push(`Agente: ${nome || filtroAgente}`);
    }
    if (filtroTipos.length) {
      const labels = filtroTipos.map(c => tipos.find(t => t.codigo === c)?.label || c);
      partes.push(`Tipos: ${labels.join(', ')}`);
    }
    if (soNaoOficializados) partes.push('Somente não-oficializados');
    return partes.length ? partes.join(' · ') : 'Todos os registros';
  }, [filtroAgente, filtroTipos, soNaoOficializados, agentes, tipos]);

  const periodoTxt = useMemo(() => {
    const de = filtroDataDe ? filtroDataDe.split('-').reverse().join('/') : '';
    const ate = filtroDataAte ? filtroDataAte.split('-').reverse().join('/') : '';
    if (de && ate) return `${de} → ${ate}`;
    if (de) return `A partir de ${de}`;
    if (ate) return `Até ${ate}`;
    return 'Todos';
  }, [filtroDataDe, filtroDataAte]);

  const supervisorNome = usuario?.nome || usuario?.email || '—';

  // ── Handlers de dialog
  const abrirCriar = () => {
    setErro(''); setSucesso(''); setErroForm('');
    setEditando(null);
    setForm(FORM_VAZIO);
    setDialogAberto(true);
  };

  const abrirEditar = (af: Afastamento) => {
    setErro(''); setSucesso(''); setErroForm('');
    setEditando(af);
    setForm({
      agente_id:              af.agente_id,
      tipo:                   af.tipo,
      data_inicio:            af.data_inicio,
      data_fim:               af.data_fim,
      data_retorno:           af.data_retorno || '',
      hora_inicio:            (af.hora_inicio || '').slice(0, 5),
      hora_fim:               (af.hora_fim || '').slice(0, 5),
      motivo:                 af.motivo || '',
      tem_comprovante:        af.tem_comprovante,
      comprovante_obs:        af.comprovante_obs || '',
      oficializado:           af.oficializado,
      observacoes_supervisor: af.observacoes_supervisor || '',
    });
    setDialogAberto(true);
  };

  const mudarTipo = (novoTipo: string) => {
    const cat = tipos.find(t => t.codigo === novoTipo);
    setForm(f => ({
      ...f,
      tipo:        novoTipo,
      oficializado: editando ? f.oficializado : (cat?.padrao_oficial ?? true),
      hora_inicio: novoTipo === 'SAIDA_TURNO' ? f.hora_inicio : '',
      hora_fim:    novoTipo === 'SAIDA_TURNO' ? f.hora_fim : '',
      data_fim:    novoTipo === 'SAIDA_TURNO' ? f.data_inicio : f.data_fim,
    }));
  };

  const mudarDataInicio = (v: string) => {
    setForm(f => ({
      ...f,
      data_inicio: v,
      data_fim:    f.tipo === 'SAIDA_TURNO' ? v : (f.data_fim || v),
    }));
  };

  const salvar = async () => {
    setErroForm('');
    if (!form.agente_id)  { setErroForm('Selecione o agente.'); return; }
    if (!form.data_inicio){ setErroForm('Informe a data de início.'); return; }
    if (!form.data_fim)   { setErroForm('Informe a data de fim.'); return; }
    if (form.data_fim < form.data_inicio) { setErroForm('Data fim não pode ser anterior à de início.'); return; }
    if (form.tipo === 'SAIDA_TURNO') {
      if (form.hora_fim && !form.hora_inicio) { setErroForm('Não preencha hora de fim sem informar hora de início.'); return; }
      if (form.hora_inicio && form.hora_fim && form.hora_fim <= form.hora_inicio) { setErroForm('Hora de fim deve ser após hora de início.'); return; }
    }

    const payload: Record<string, unknown> = {
      tipo:                   form.tipo,
      data_inicio:            form.data_inicio,
      data_fim:               form.data_fim,
      data_retorno:            form.data_retorno || null,
      hora_inicio: form.tipo === 'SAIDA_TURNO' ? (form.hora_inicio ? `${form.hora_inicio}:00` : null) : null,
      hora_fim:    form.tipo === 'SAIDA_TURNO' ? (form.hora_fim    ? `${form.hora_fim}:00`    : null) : null,
      motivo:                 form.motivo || null,
      tem_comprovante:        form.tem_comprovante,
      comprovante_obs:        form.tem_comprovante ? (form.comprovante_obs || null) : null,
      oficializado:           form.oficializado,
      observacoes_supervisor: form.observacoes_supervisor || null,
    };
    if (!editando) payload.agente_id = form.agente_id;

    setSalvando(true);
    try {
      if (editando) {
        await axios.put(`${API_URL}/api/afastamentos/${editando.id}`, payload, { headers });
        setSucesso('Afastamento atualizado.');
      } else {
        await axios.post(`${API_URL}/api/afastamentos`, payload, { headers });
        setSucesso('Afastamento criado.');
      }
      setDialogAberto(false);
      carregar();
      carregarTodos();
      recarregarFichaSeAberta();
    } catch (e) {
      setErroForm(extrairErro(e, 'Erro ao salvar.'));
    } finally {
      setSalvando(false);
    }
  };

  const confirmarExclusao = async () => {
    if (!dialogExcluir) return;
    setExcluindo(true);
    try {
      await axios.delete(`${API_URL}/api/afastamentos/${dialogExcluir.id}`, { headers });
      setSucesso('Afastamento excluído.');
      setDialogExcluir(null);
      carregar();
      carregarTodos();
      recarregarFichaSeAberta();
    } catch (e) {
      setErro(extrairErro(e, 'Erro ao excluir.'));
    } finally {
      setExcluindo(false);
    }
  };

  const limparFiltros = () => {
    setFiltroAgente('');
    setFiltroTipos([]);
    setFiltroDataDe('');
    setFiltroDataAte('');
    setSoNaoOficializados(false);
  };

  // ── Derivados do redesenho (27/10/2026)
  const hoje = hojeLocal();
  const alertas = useMemo(() => alertasConferir(todos, hoje), [todos, hoje]);
  const qtdPorVir = registros.filter(r => situacao(r, hoje) !== 'passado').length;
  const qtdPassou = registros.length - qtdPorVir;
  const codigosCatalogo = tipos.map(t => t.codigo);
  const grupoAtual = grupoDosCodigos(filtroTipos, codigosCatalogo);
  const escolherGrupo = (g: GrupoTipo) => setFiltroTipos(codigosDoGrupo(g, codigosCatalogo));

  // ════════════════════════════════════════════════════════════
  // RENDER
  // ════════════════════════════════════════════════════════════

  return (
    <Box sx={{ backgroundColor: '#F5F5F7', minHeight: '100vh' }}>
      <ModuleHeader
        titulo={vista === 'ficha' && fichaResumo ? `Ficha — ${fichaResumo.agente_nome}` : 'Férias e Licenças'}
        subtitulo={vista === 'ficha' ? 'Histórico individual do agente' : 'Afastamentos, faltas e saídas de turno'}
        icone={vista === 'ficha' ? <PersonIcon /> : <EventBusyIcon />}
        onVoltar={vista === 'ficha' ? voltarParaLista : onVoltar}
      />

      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>

        {vista === 'ficha' ? (
          <FichaAgente
            carregando={fichaCarregando}
            resumo={fichaResumo}
            registros={fichaRegistros}
            onEditar={abrirEditar}
            onExcluir={(af) => setDialogExcluir(af)}
            onVoltar={voltarParaLista}
            onImprimir={() => setDialogImprimir(true)}
          />
        ) : (
        <>

        {/* Faixa Hoje (substitui os 4 cartões antigos; calculada aqui, com a lista completa) */}
        <FaixaHoje todos={todos} hoje={hoje} total={equipe ? equipe.total : null} idsEquipe={equipe ? equipe.ids : undefined} />

        {/* Mensagens globais */}
        {erro    && <Alert severity="error"   sx={{ mb: 2 }} onClose={() => setErro('')}>{erro}</Alert>}
        {sucesso && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSucesso('')}>{sucesso}</Alert>}

        {/* Linha de ação primária */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<PrintIcon />}
            onClick={() => setDialogImprimir(true)}
            disabled={registros.length === 0}
            sx={{ borderRadius: 2 }}
          >
            Imprimir relatório
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={abrirCriar}
            sx={{ backgroundColor: '#0071E3', borderRadius: 2 }}
          >
            Novo registro
          </Button>
        </Box>

        {/* Abas */}
        <AbasAfastamentos aba={aba} onTrocar={trocarAba} qtdPorVir={qtdPorVir} qtdPassou={qtdPassou} />

        {/* Filtros (só Por vir e Já passou; o calendário ignora filtros) */}
        {aba !== 'calendario' && (
        <Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 3, border: '1px solid #E5E7EB' }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ alignItems: { md: 'center' }, flexWrap: 'wrap' }}>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Agente</InputLabel>
              <Select
                label="Agente"
                value={filtroAgente}
                onChange={e => setFiltroAgente(e.target.value as '' | number)}
              >
                <MenuItem value=""><em>Todos</em></MenuItem>
                {agentes.map(a => <MenuItem key={a.id} value={a.id}>{a.nome}</MenuItem>)}
              </Select>
            </FormControl>

            <Box role="group" aria-label="Tipo de afastamento" sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {GRUPOS_TIPO.map(g => {
                const ativo = grupoAtual === g.valor;
                return (
                  <Button
                    key={g.valor}
                    aria-pressed={ativo}
                    onClick={() => escolherGrupo(g.valor)}
                    sx={{
                      minHeight: 44, px: 2, borderRadius: 999, textTransform: 'none', fontWeight: 700, fontSize: 15,
                      backgroundColor: ativo ? AZUL_ABA : 'white',
                      color: ativo ? 'white' : '#374151',
                      border: ativo ? `2px solid ${AZUL_ABA}` : '2px solid #D1D5DB',
                      '&:hover': { backgroundColor: ativo ? '#1565c0' : '#F3F4F6' },
                    }}
                  >
                    {g.rotulo}
                  </Button>
                );
              })}
            </Box>

            <Box sx={{ flexGrow: 1 }} />
            <Button
              variant="text"
              onClick={() => setMaisFiltros(v => !v)}
              aria-expanded={maisFiltros}
              sx={{ minHeight: 44, textTransform: 'none', fontWeight: 600 }}
            >
              {maisFiltros ? 'Menos filtros' : 'Mais filtros (datas, não-oficializados)'}
              {!maisFiltros && [filtroDataDe, filtroDataAte, soNaoOficializados].filter(Boolean).length > 0
                ? ` · ${[filtroDataDe, filtroDataAte, soNaoOficializados].filter(Boolean).length} em uso` : ''}
            </Button>
          </Stack>

          {maisFiltros && (
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ alignItems: { md: 'center' }, flexWrap: 'wrap', mt: 2 }}>
              <TextField
                size="small" type="date" label="De"
                value={filtroDataDe}
                onChange={e => setFiltroDataDe(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ minWidth: 140 }}
              />
              <TextField
                size="small" type="date" label="Até"
                value={filtroDataAte}
                onChange={e => setFiltroDataAte(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }}
                sx={{ minWidth: 140 }}
              />

              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={soNaoOficializados}
                    onChange={e => setSoNaoOficializados(e.target.checked)}
                  />
                }
                label="Só não-oficializados"
                sx={{ '& .MuiFormControlLabel-label': { fontSize: 13 } }}
              />

              <Box sx={{ flexGrow: 1 }} />
              <Button size="small" variant="text" onClick={limparFiltros} sx={{ minHeight: 44 }}>Limpar</Button>
            </Stack>
          )}
        </Paper>
        )}

        {/* Conteúdo da aba */}
        {aba === 'calendario' ? (
          <CalendarioEquipe todos={todos} hoje={hoje} total={equipe ? equipe.total : null} idsEquipe={equipe ? equipe.ids : undefined} />
        ) : carregando ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : aba === 'por_vir' ? (
          <ListaPorVir registros={registros} hoje={hoje} alertas={alertas}
            onEditar={abrirEditar} onExcluir={af => setDialogExcluir(af)} onAbrirAgente={abrirFicha} />
        ) : (
          <TabelaJaPassou registros={registros} hoje={hoje} alertas={alertas}
            onEditar={abrirEditar} onExcluir={af => setDialogExcluir(af)} onAbrirAgente={abrirFicha} />
        )}

        </>
        )}

        {/* ── Dialog Criar / Editar ── */}
        <Dialog open={dialogAberto} onClose={() => setDialogAberto(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 700, color: C.azul }}>
            {editando ? 'Editar registro' : 'Novo registro'}
          </DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2} sx={{ mt: 1 }}>

              {erroForm && <Alert severity="error">{erroForm}</Alert>}

              <FormControl fullWidth size="small" disabled={!!editando}>
                <InputLabel>Agente *</InputLabel>
                <Select
                  label="Agente *"
                  value={form.agente_id}
                  onChange={e => setForm(f => ({ ...f, agente_id: e.target.value as '' | number }))}
                >
                  <MenuItem value=""><em>Selecione</em></MenuItem>
                  {agentes.map(a => <MenuItem key={a.id} value={a.id}>{a.nome}</MenuItem>)}
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Tipo *</InputLabel>
                <Select label="Tipo *" value={form.tipo} onChange={e => mudarTipo(e.target.value)}>
                  {tipos.map(t => <MenuItem key={t.codigo} value={t.codigo}>{t.label}</MenuItem>)}
                </Select>
              </FormControl>

              <Stack direction="row" spacing={2}>
                <TextField
                  fullWidth size="small" type="date" label="Data início *"
                  value={form.data_inicio}
                  onChange={e => mudarDataInicio(e.target.value)}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
                <TextField
                  fullWidth size="small" type="date" label="Data fim *"
                  value={form.data_fim}
                  onChange={e => setForm(f => ({ ...f, data_fim: e.target.value }))}
                  slotProps={{ inputLabel: { shrink: true } }}
                  disabled={form.tipo === 'SAIDA_TURNO'}
                />
                <TextField
                  fullWidth size="small" type="date" label="Data de Retorno"
                  value={form.data_retorno}
                  onChange={e => setForm(f => ({ ...f, data_retorno: e.target.value }))}
                  slotProps={{ inputLabel: { shrink: true } }}
                />
              </Stack>

              {form.tipo === 'SAIDA_TURNO' && (
                <Stack direction="row" spacing={2}>
                  <TextField
                    fullWidth size="small" type="time" label="Hora início (opcional)"
                    value={form.hora_inicio}
                    onChange={e => setForm(f => ({ ...f, hora_inicio: e.target.value }))}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                  <TextField
                    fullWidth size="small" type="time" label="Hora fim (se voltou)"
                    value={form.hora_fim}
                    onChange={e => setForm(f => ({ ...f, hora_fim: e.target.value }))}
                    slotProps={{ inputLabel: { shrink: true } }}
                  />
                </Stack>
              )}

              <TextField
                fullWidth size="small" multiline minRows={2}
                label="Motivo"
                placeholder="Ex: consulta médica agendada"
                value={form.motivo}
                onChange={e => setForm(f => ({ ...f, motivo: e.target.value }))}
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={form.tem_comprovante}
                    onChange={e => setForm(f => ({ ...f, tem_comprovante: e.target.checked }))}
                  />
                }
                label="Tem comprovante"
              />
              {form.tem_comprovante && (
                <TextField
                  fullWidth size="small"
                  label="Descrição do comprovante"
                  placeholder="Ex: atestado médico Dr. Silva — 2 dias"
                  value={form.comprovante_obs}
                  onChange={e => setForm(f => ({ ...f, comprovante_obs: e.target.value }))}
                />
              )}

              <FormControlLabel
                control={
                  <Switch
                    checked={form.oficializado}
                    onChange={e => setForm(f => ({ ...f, oficializado: e.target.checked }))}
                  />
                }
                label={
                  <Typography sx={{ fontSize: 14 }}>
                    {form.oficializado
                      ? 'Oficializado — envia à gestão municipal'
                      : 'Interno — só registro do supervisor'}
                  </Typography>
                }
              />

              <TextField
                fullWidth size="small" multiline minRows={2}
                label="Observações do supervisor (interno)"
                value={form.observacoes_supervisor}
                onChange={e => setForm(f => ({ ...f, observacoes_supervisor: e.target.value }))}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setDialogAberto(false)} disabled={salvando}>Cancelar</Button>
            <Button
              variant="contained" onClick={salvar} disabled={salvando}
              sx={{ backgroundColor: '#065F46', borderRadius: 2 }}
            >
              {salvando
                ? <CircularProgress size={18} sx={{ color: 'white' }} />
                : editando ? 'Salvar' : 'Criar'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Dialog Excluir ── */}
        <Dialog open={!!dialogExcluir} onClose={() => setDialogExcluir(null)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 700, color: '#991B1B' }}>Excluir registro?</DialogTitle>
          <DialogContent>
            {dialogExcluir && (
              <Box>
                <Alert severity="warning" sx={{ mb: 1 }}>Esta ação não pode ser desfeita.</Alert>
                <Typography sx={{ fontSize: 13, color: '#374151', mt: 1 }}>
                  <strong>{dialogExcluir.agente_nome}</strong> — {dialogExcluir.tipo_label}
                </Typography>
                <Typography sx={{ fontSize: 13, color: '#6B7280' }}>
                  {fmtPeriodo(dialogExcluir)}
                  {dialogExcluir.dias_corridos > 1 ? ` (${dialogExcluir.dias_corridos} dias)` : ''}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setDialogExcluir(null)} disabled={excluindo}>Cancelar</Button>
            <Button variant="contained" color="error" onClick={confirmarExclusao} disabled={excluindo}>
              {excluindo ? <CircularProgress size={18} sx={{ color: 'white' }} /> : 'Excluir'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Dialog explicativo antes de imprimir ── */}
        <Dialog open={dialogImprimir} onClose={() => setDialogImprimir(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontWeight: 700, color: C.azul }}>Antes de imprimir</DialogTitle>
          <DialogContent dividers>
            <Typography sx={{ fontSize: 14, mb: 2 }}>
              Para um relatório <strong>sem cabeçalho/rodapé do navegador</strong> (URL, data, número de página):
            </Typography>
            <Box sx={{ pl: 1, fontSize: 13, color: '#374151' }}>
              <Box sx={{ mb: 1 }}>1. Na janela que vai abrir, clique em <strong>Mais configurações</strong></Box>
              <Box sx={{ mb: 1 }}>2. <strong>Desmarque</strong> a opção <em>"Cabeçalhos e rodapés"</em></Box>
              <Box sx={{ mb: 1 }}>3. Em <em>"Margens"</em>, escolha <strong>Padrão</strong> ou <strong>Mínima</strong></Box>
            </Box>
            <Typography sx={{ fontSize: 12, color: '#6B7280', mt: 2, fontStyle: 'italic' }}>
              Para gerar PDF: na opção <em>"Destino"</em>, escolha <strong>"Salvar como PDF"</strong>.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2 }}>
            <Button onClick={() => setDialogImprimir(false)}>Cancelar</Button>
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={dispararImpressao}
              sx={{ backgroundColor: '#1B3A6B', borderRadius: 2 }}
            >
              Abrir impressão
            </Button>
          </DialogActions>
        </Dialog>

        {/* ── Área de impressão (oculta na tela, visível no @media print) ── */}
        {vista === 'ficha' && fichaResumo ? (
          <RelatorioImpressao
            modo="ficha"
            titulo={fichaResumo.agente_nome}
            subtitulo="Histórico anual de férias, licenças, faltas e saídas de turno. Agente de Combate a Endemias — Milagres, BA."
            resumo={fichaResumo}
            registros={fichaRegistros}
            supervisorNome={supervisorNome}
          />
        ) : (
          <RelatorioImpressao
            modo="geral"
            titulo="Relatório geral de afastamentos."
            subtitulo="Consolidação de férias, licenças, faltas e saídas de turno da equipe de Agentes de Combate a Endemias do município de Milagres — BA."
            filtrosTxt={filtrosTxt}
            periodoTxt={periodoTxt}
            registros={registros}
            supervisorNome={supervisorNome}
          />
        )}

      </Box>
    </Box>
  );
}
